import { useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";

import { AppModel } from "../../../app/app-model";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { AdminConsole } from "../admin-console";
import { ClinicsModal } from "../common/clinics-modal";
import { PhysiciansModal } from "../common/physicians-modal";

/** The two assignment lists, as `PUT /users/{uuid}` names them. */
export interface AssignmentDraft {
  physicians: string[];
  clinics: string[];
}

/**
 * Which physicians and clinics a user is assigned to - the rows the new-user
 * wizard and the user edit dialog both need, so they stay one pair of controls
 * with one pair of labels.
 *
 * This is the *assignment* relation, a different list from the visibility scope
 * the permissions dialog edits.
 *
 * Rows, not a form: it writes into the caller's draft and leaves saving to
 * whichever dialog hosts it.
 */
export const AssignmentRows = observer(function AssignmentRows({
  draft,
  admin,
  username,
  userUuid,
  app,
}: {
  draft: AssignmentDraft;
  admin: AdminConsole;
  /** Names the user in the pickers' titles. */
  username: string;
  /** Keeps the user's own physician record out of the picker, see below. */
  userUuid: string;
  /** When given, the pickers also offer creating a record that does not exist yet. */
  app?: AppModel;
}) {
  const { t } = useLingui();

  // The pickers hold throwaway list models, so there is nothing of theirs to
  // refresh after an inline create - it selects the new record and confirms
  // straight away. What would otherwise go stale is the admin console's own
  // lists, which the clinics and physicians routes render.
  const refreshConsole = app
    ? {
        physicians: () => admin.refreshPhysicians(),
        clinics: () => admin.refreshClinics(),
      }
    : undefined;

  return (
    <>
      <FormRow
        label={t`Physicians`}
        hint={t`The physicians this user is assigned to.`}
        data={
          <ChooseField
            count={draft.physicians.length}
            modal={({ onClose }) => (
              <PhysiciansModal
                title={t`Choose physicians for: ${username}`}
                preselected={draft.physicians}
                mode="multi"
                // A login assigned to its own physician record becomes a
                // monitoring user of itself, which no write path rejects. Hide
                // it unless it is already assigned, so that can still be undone.
                model={admin.createPhysicians(
                  (p) =>
                    p.connected_user?.uuid === userUuid &&
                    !draft.physicians.includes(p.uuid),
                )}
                allowCreate={app != null}
                app={app}
                onAfterCreate={refreshConsole?.physicians}
                onClose={onClose}
                onConfirm={({ selected }) => {
                  draft.physicians = selected;
                }}
              />
            )}
          />
        }
      />

      <FormRow
        label={t`Clinics`}
        hint={t`The clinics this user is assigned to.`}
        data={
          <ChooseField
            count={draft.clinics.length}
            modal={({ onClose }) => (
              <ClinicsModal
                title={t`Choose clinics for: ${username}`}
                preselected={draft.clinics}
                mode="multi"
                model={admin.createClinics()}
                allowCreate={app != null}
                admin={admin}
                onAfterCreate={refreshConsole?.clinics}
                onClose={onClose}
                onConfirm={({ selected }) => {
                  draft.clinics = selected;
                }}
              />
            )}
          />
        }
      />
    </>
  );
});
